/* eslint react/prop-types: 0 */
/* eslint camelcase: 0 */
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';

import format from '../../../utilities/FormattingUtilities';
import { green, yellow, orange, red, gray, getWorstStatusColor } from '../../../utilities/StatusUtilities';

function getInvertedStatusColor(value, thresholds) {
    if (_.isNumber(value) && thresholds) {
        if (value > thresholds.degraded) {
            return green.value;
        }

        if (value <= thresholds.degraded && value > thresholds.critical) {
            return yellow.value;
        }

        return orange.value;
    }

    return gray.value;
}

function getStatusColorBasedOnThresholds(value, thresholds) {
    if (thresholds.inverted) {
        return getInvertedStatusColor(value, thresholds);
    }

    if (_.isNumber(value) && thresholds) {
        if (value >= thresholds.critical) {
            return orange.value;
        }

        if (value >= thresholds.degraded) {
            return yellow.value;
        }

        return green.value;
    }

    return gray.value;
}

function getStatusColor(col, row, thresholds) {
    // We can't calculate RED here, so if the test status is red then just return that.
    const statusColor = getWorstStatusColor(row?.status);
    if (statusColor === red.value || statusColor === gray.value) {
        return statusColor;
    }

    // For BTT tests, only color failed steps red (distinguished by '-2' as metric value).
    const failedStep = row?.failed_step;
    const metricName = col?.mapping;
    if (!_.isEmpty(failedStep) && failedStep === metricName) {
        return red.value;
    }

    // If we don't have thresholds then just return empty which will be black
    if (!thresholds || _.isEmpty(thresholds)) {
        return '';
    }

    return getStatusColorBasedOnThresholds(col.mapper(row, col), thresholds);
}

export function getThresholds(thresholdMetrics, row) {
    if (
        thresholdMetrics.critical &&
        thresholdMetrics.degraded &&
        _.isNumber(row[thresholdMetrics.critical]) &&
        _.isNumber(row[thresholdMetrics.degraded])
    ) {
        return {
            inverted: thresholdMetrics.inverted,
            critical: row[thresholdMetrics.critical],
            degraded: row[thresholdMetrics.degraded],
        };
    }

    return null;
}

const useStyles = makeStyles((theme) => ({
    green: {
        borderBottom: `3px solid ${theme.palette.status.green}`,
    },

    yellow: {
        borderBottom: `3px solid ${theme.palette.status.yellow}`,
    },

    orange: {
        borderBottom: `3px solid ${theme.palette.status.orange}`,
    },

    red: {
        borderBottom: '3px',
    },

    gray: {
        borderBottom: '3px',
    },
}));

function MetricStatus({ statusColor, metricValue }) {
    const classes = useStyles();
    return <span className={classes[statusColor]}>{metricValue}</span>;
}

function metricStatusRenderer(thresholdGetter, row, col) {
    const thresholds = thresholdGetter(row);
    const statusColor = getStatusColor(col, row, thresholds);
    const { mapper, formatter } = col;
    let value = mapper(row, col);
    if (formatter) {
        if (_.isFunction(formatter)) {
            value = formatter(value, row);
        } else {
            value = format(value, _.isString(formatter) ? { type: formatter } : formatter);
        }
    }
    return <MetricStatus statusColor={statusColor} metricValue={value} />;
}

export function getMetricStatusRendererWithThresholds(thresholds) {
    const thresholdGetter = () => thresholds;
    return _.partial(metricStatusRenderer, thresholdGetter);
}

export function getMetricStatusRendererWithThresholdMetrics(test, thresholdMetrics) {
    const thresholdGetter = _.partial(getThresholds, thresholdMetrics);
    return _.partial(metricStatusRenderer, thresholdGetter);
}
